import { BALANCE } from '../sim/balance';
import { ITEMS } from '../sim/items';
import type { Consignment, DropoffDef } from '../sim/round';
import type { PlayerState, RoundPhase, Vec3 } from '../sim/types';

/**
 * WHAT AM I MEANT TO BE DOING.
 *
 * Everything the HUD showed before this file existed was true and none of it was
 * an instruction. A player who has never been here reads `$0`, `NOTHING ON YOU`,
 * `DOORS OPEN IN 0:14` and a card headed TRANSFER HELD PENDING PAPERWORK, and
 * correctly concludes that a heist is happening — without learning that they are
 * supposed to pick up two tools off the table behind them, or that loot in your
 * hands is worth nothing until it goes down a chute. The only plain sentence in
 * the build was buried in the pause menu, which is the one screen you reach by
 * having already stopped playing.
 *
 * So: ONE line, on screen the whole time, that names the next physical action and
 * the place to do it. Not a tutorial, not a popup, not a sequence of gated steps.
 * It is derived from state rather than scripted, which means it cannot desync
 * from the game and it keeps working on the fortieth night — by then it has
 * stopped being instructions and become a glance that says which chute is nearest.
 *
 * THE FLAVOUR STAYS. The corporate signage is the whole tone of the thing and it
 * is not being replaced; the objective sits alongside it. The briefing card still
 * says TRANSFER HELD PENDING PAPERWORK. It just says what that means underneath.
 *
 * Guards get a line and no waypoint on purpose. Telling a guard where the thieves
 * are is not a clarity fix, it is a different game.
 */

export interface Objective {
  /** The imperative. Short enough to read at a sprint. */
  verb: string;
  /** Where, or what it pays. One line, no wrapping. */
  detail: string;
  /** A point in the building to steer toward, or null when there is none. */
  at: Vec3 | null;
  /** What the marker over that point calls it. */
  mark: string;
  /** Set when the clock is the problem, so the HUD can turn it red. */
  urgent: boolean;
}

export interface ObjectiveInput {
  phase: RoundPhase;
  clock: number;
  player: PlayerState;
  /** Tonight's delivery, and where in the building it landed. */
  consignment: Consignment | null;
  consignmentAt: Vec3 | null;
  /** The loadout table in the car park. */
  bench: Vec3;
  dropoffs: readonly DropoffDef[];
  /** Still in the building, including whoever is reading this. */
  thievesLeft: number;
}

const DROP_LABEL: Record<DropoffDef['kind'], string> = {
  solo: 'CHUTE',
  dock: 'LOADING DOCK',
  // It was 'FIRE EXIT' back when the way out was an unmarked disc on the tarmac.
  // It is now a van with its doors open in the car park, and the marker points
  // straight at it — a label that names a different object than the one the
  // arrow lands on is worse than no label.
  extraction: 'THE VAN',
};

/** Straight-line metres. Vertical counts: the mall has two floors. */
function dist(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * The drop-off to point at.
 *
 * Nearest wins, adjusted by what each one is actually worth, in metres — a flat
 * handicap rather than a ratio, because the payoff difference is flat too.
 *
 * The dock gets a head start it has earned: full rate against the chute's 65%,
 * so a dock a little further is still the better walk and the arrow should say
 * so. The fire exit gets a large penalty and it is not really about the 45%. An
 * extraction ENDS YOUR ROUND. Sending a player through one because it happened
 * to be six metres away is the single worst piece of advice this line could
 * give, and a first-time player following the marker would take it. It stays in
 * the list so the arrow can still find it when nothing else is remotely near,
 * and it has to beat everything else by half a mall to get picked.
 */
const HANDICAP_M: Record<DropoffDef['kind'], number> = { dock: 14, solo: 0, extraction: -45 };

function bestDropoff(from: Vec3, dropoffs: readonly DropoffDef[]): DropoffDef | null {
  let best: DropoffDef | null = null;
  let bestCost = Infinity;
  for (const d of dropoffs) {
    const cost = dist(from, d.pos) - HANDICAP_M[d.kind];
    if (cost < bestCost) {
      bestCost = cost;
      best = d;
    }
  }
  return best;
}

/** Tonight's next physical action, as the player should read it. */
export function objectiveFor(s: ObjectiveInput): Objective | null {
  if (s.phase === 'lobby' || s.phase === 'results') return null;

  const p = s.player;

  if (p.team === 'guard') {
    if (s.phase === 'briefing') {
      return {
        verb: 'WALK YOUR BEAT',
        detail: 'They come in when the clock hits zero. Cameras are in the control room.',
        at: null,
        mark: '',
        urgent: false,
      };
    }
    return {
      verb: 'CATCH THEM',
      detail:
        s.thievesLeft > 0
          ? `${s.thievesLeft} inside · Get close and hold E to take one down`
          : 'Building clear',
      at: null,
      mark: '',
      urgent: false,
    };
  }

  // A thief who is out of the round is told so, rather than being handed an
  // instruction they cannot follow. Silence here reads as the HUD breaking.
  if (p.status !== 'active') {
    return {
      verb: p.status === 'extracted' ? 'YOU ARE OUT' : 'CAUGHT',
      detail:
        p.status === 'extracted'
          ? 'Banked and clear. Watch the rest of it happen.'
          : 'Your night is over. Anything you were carrying is on the floor where you fell.',
      at: null,
      mark: '',
      urgent: false,
    };
  }

  if (s.phase === 'briefing') {
    const need = 2 - p.tools.length;
    if (need > 0) {
      return {
        verb: need === 2 ? 'TAKE TWO TOOLS' : 'TAKE ONE MORE TOOL',
        detail: 'Off the bench. Look at one and press E. No tool, no shutters.',
        at: s.bench,
        mark: 'LOADOUT',
        urgent: false,
      };
    }
    return {
      verb: 'WAIT FOR THE DOORS',
      detail: 'Kitted out. The mall opens when the clock runs out.',
      at: null,
      mark: '',
      urgent: false,
    };
  }

  const carriedValue = p.carried.reduce((n, x) => n + (ITEMS[x.item]?.value ?? 0), 0);
  const lowClock = s.clock <= BALANCE.round.warnAtSec;

  if (carriedValue > 0) {
    const drop = bestDropoff(p.pos, s.dropoffs);
    const rate =
      drop?.kind === 'dock'
        ? '100%'
        : drop?.kind === 'extraction'
          ? '45%'
          : `${Math.round(BALANCE.dropoff.solo.payoutRate * 100)}%`;
    const worth = `$${carriedValue.toLocaleString()}`;
    const where = DROP_LABEL[drop?.kind ?? 'solo'].toLowerCase();
    return {
      verb: lowClock ? 'GET IT OUT NOW' : 'CASH IT IN',
      detail: lowClock
        ? `${worth} on you, worth nothing until you post it`
        : drop?.kind === 'extraction'
          ? // Named as the trade it is, because the marker is pointing at the one
            // drop-off you cannot come back from.
            `${worth} on you · ${where[0].toUpperCase()}${where.slice(1)} pays ${rate} and ends your night`
          : `${worth} on you · Hold E at the ${where} for ${rate}`,
      at: drop?.pos ?? null,
      mark: drop ? DROP_LABEL[drop.kind] : '',
      urgent: lowClock,
    };
  }

  // The doors opened and you took nothing off the bench. Every shutter in the
  // building is shut against you, so "ROB SOMETHING" is an instruction you
  // physically cannot follow, pointing at a store you cannot get into — the
  // exact dead end this file exists to prevent. The bench is still laid out on
  // the forecourt during the round, so send them back for one.
  if (p.tools.length === 0) {
    return {
      verb: 'GET A TOOL FIRST',
      detail: 'Nothing in here opens without one. The bench is still out on the forecourt.',
      at: s.bench,
      mark: 'LOADOUT',
      urgent: lowClock,
    };
  }

  // Empty-handed and the mall is open. This is the moment the whole design
  // hinges on, so it names the one store that is different tonight.
  const c = s.consignment;
  if (c && s.consignmentAt) {
    const item = ITEMS[c.item]?.name ?? c.item;
    return {
      verb: 'ROB SOMETHING',
      detail: `Tonight: ${c.count} × ${item} in ${c.storeName} · $${c.value.toLocaleString()}`,
      at: s.consignmentAt,
      mark: c.storeName.toUpperCase(),
      urgent: lowClock,
    };
  }
  return {
    verb: 'ROB SOMETHING',
    detail: 'Break a shutter, open the cases, fill your slots. Richer stores lock harder.',
    at: null,
    mark: '',
    urgent: lowClock,
  };
}
